import { useCallback, useMemo, Fragment, ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import type { AssistantMarkdownImage, AssistantMessageLink, AssistantSuggestion } from "../../types/assistant";
import type { Photo, Video } from "../../types";
import { PhotoWidget } from "../PhotoWidget";
import { SceneImageCard } from "../SceneImageCard";
import { SceneBannerCard } from "../SceneBannerCard";
import { PersonSimpleRunIcon } from "../SceneBannerCard/icons";
import { SceneBannerSlider } from "../SceneBannerSlider";
import { useI18n } from "../../i18n";
import {
  getSceneBannerKind,
  resolveBannerLocationKey,
  SCENE_BANNER_BUTTON_LABEL_KEYS,
  type SceneBannerKind,
} from "../../scene/sceneBannerMap";
import styles from "./MarkdownBlock.module.css";

const remarkPlugins = [remarkGfm];

type MarkdownBlockProps = {
  content: string;
  images: AssistantMarkdownImage[];
  links?: AssistantMessageLink[];
  onPhotosClick: (items: Array<Photo | Video>) => void;
  onLinkClick?: (suggestion: AssistantSuggestion) => void;
  onEnterScene?: (sceneId: string) => void;
};

const IMAGE_LINE_RE = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/;
const SG_LINK_RE = /^sg_/;

type TextSegment = { type: "text"; content: string };
type ImageGroupSegment = { type: "images"; srcs: string[] };
type ContentSegment = TextSegment | ImageGroupSegment;

// Local buffer for consecutive main/inside banners before flush into
// `SceneBannerSlider`. Was previously exported as `SceneBannerSliderItem`
// from `SceneBannerSlider` itself; now lives here since the slider's API
// is generic over arbitrary slide nodes.
type SceneBannerBufferItem = {
  img: AssistantMarkdownImage;
  buttonLabel: string;
  buttonIcon: ReactNode;
};

const splitContentSegments = (content: string): ContentSegment[] => {
  const lines = content.split("\n");
  const segments: ContentSegment[] = [];
  let textLines: string[] = [];

  const flushText = () => {
    if (textLines.length > 0) {
      const text = textLines.join("\n").trim();
      if (text) {
        segments.push({ type: "text", content: text });
      }
      textLines = [];
    }
  };

  for (const line of lines) {
    const match = line.match(IMAGE_LINE_RE);
    if (match) {
      const src = match[2];
      const last = segments[segments.length - 1];
      if (textLines.length === 0 && last?.type === "images") {
        last.srcs.push(src);
      } else {
        flushText();
        segments.push({ type: "images", srcs: [src] });
      }
    } else {
      textLines.push(line);
    }
  }

  flushText();
  return segments;
};

const toPhoto = (image: AssistantMarkdownImage): Photo => ({
  type: "photo",
  id: image.id,
  src: image.src,
  width: image.width,
  height: image.height,
  caption: image.caption ?? undefined,
  title: image.title ?? undefined,
  description: image.description ?? undefined,
  thumbnailSrc: image.thumbnailSrc ?? undefined,
});

const getTextContent = (node: ReactNode): string => {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getTextContent).join("");
  if (node && typeof node === "object" && "props" in node) {
    return getTextContent((node as { props: { children?: ReactNode } }).props.children);
  }
  return "";
};

const linkToSuggestion = (link: AssistantMessageLink, fallbackLabel: string): AssistantSuggestion => {
  if (link.type === "sight") {
    return {
      type: "sight",
      sightId: link.sightId,
      suggestionId: link.suggestionId,
    };
  }
  return {
    type: "more",
    label: link.label ?? fallbackLabel,
    data: link.data,
    suggestionId: link.suggestionId,
  };
};

export const MarkdownBlock = ({
  content,
  images,
  links,
  onPhotosClick,
  onLinkClick,
  onEnterScene,
}: MarkdownBlockProps) => {
  const i18n = useI18n();

  // Строит текст кнопки баннера. main и inside используют шаблон
  // `exploreLocation` ("Explore {name}"); имя подставляется из
  // `globalMap.markerLabels` по ключу из `resolveBannerLocationKey`
  // (main → URL-id сцены, inside → id её родителя). Для artifact
  // шаблон без подстановки — `.replace` отрабатывает no-op.
  const buildButtonLabel = useCallback(
    (kind: SceneBannerKind, sceneId: string): string => {
      const template = i18n.assistant[SCENE_BANNER_BUTTON_LABEL_KEYS[kind]];
      const locationKey = resolveBannerLocationKey(kind, sceneId);
      const name = locationKey ? i18n.globalMap.markerLabels[locationKey] ?? "" : "";
      return template.replace("{name}", name);
    },
    [i18n],
  );

  const imageMap = useMemo(
    () => new Map(images.map((img) => [img.id, img])),
    [images],
  );

  const linkMap = useMemo(
    () => new Map((links ?? []).map((link) => [link.suggestionId, link])),
    [links],
  );

  const components = useMemo((): Components => ({
    a: ({ href, children }) => {
      if (href && SG_LINK_RE.test(href)) {
        const link = linkMap.get(href);
        if (link && onLinkClick) {
          const handleClick = () => {
            const label = getTextContent(children);
            onLinkClick(linkToSuggestion(link, label));
          };
          return (
            <span
              role="button"
              tabIndex={0}
              className={styles.suggestionLink}
              onClick={handleClick}
              onPointerDown={(e) => e.preventDefault()}
              onKeyDown={(e) => { if (e.key === "Enter") handleClick(); }}
            >
              {children}
            </span>
          );
        }
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  }), [linkMap, onLinkClick]);

  const segments = useMemo(() => splitContentSegments(content), [content]);

  const nodes = useMemo((): ReactNode[] =>
    segments.map((segment, i) => {
      if (segment.type === "images") {
        const allImages = segment.srcs
          .map((src) => imageMap.get(src))
          .filter((meta): meta is AssistantMarkdownImage => Boolean(meta));

        if (allImages.length === 0) {
          return null;
        }

        type BannerImage = { img: AssistantMarkdownImage; kind: SceneBannerKind };
        const bannerImages: BannerImage[] = [];
        const regularImages: AssistantMarkdownImage[] = [];

        for (const img of allImages) {
          const sceneId = img.moveToSceneId;
          const kind = onEnterScene && sceneId ? getSceneBannerKind(sceneId) : null;
          if (kind) {
            bannerImages.push({ img, kind });
          } else {
            regularImages.push(img);
          }
        }

        const items: ReactNode[] = [];

        // Buffer consecutive scene-banner cards (`inside` / `main`). When the
        // buffer holds 2+ items, render them as a horizontal `SceneBannerSlider`
        // (matches Figma's "Free Roam" mode). A single banner falls back to a
        // full-width `SceneBannerCard`. Artifact cards interrupt the buffer:
        // they're rendered inline as before, so the original order
        // `[banner, banner, artifact, banner]` becomes
        // `[Slider(2), Artifact, Card(1)]`.
        let sceneBannerBuffer: SceneBannerBufferItem[] = [];
        const flushSceneBanners = () => {
          if (sceneBannerBuffer.length === 0) return;
          if (sceneBannerBuffer.length >= 2) {
            const firstImg = sceneBannerBuffer[0].img;
            const slides = sceneBannerBuffer.map(({ img, buttonLabel, buttonIcon }) => ({
              key: img.id,
              node: (
                <SceneBannerCard
                  compact
                  cardClickable
                  image={img}
                  buttonLabel={buttonLabel}
                  buttonIcon={buttonIcon}
                  onEnterScene={onEnterScene!}
                />
              ),
            }));
            items.push(
              <SceneBannerSlider
                key={`banner-slider-${i}-${firstImg.id}`}
                slides={slides}
              />,
            );
          } else {
            const only = sceneBannerBuffer[0];
            items.push(
              <SceneBannerCard
                key={`banner-${only.img.id}`}
                cardClickable
                image={only.img}
                buttonLabel={only.buttonLabel}
                buttonIcon={only.buttonIcon}
                onEnterScene={onEnterScene!}
              />,
            );
          }
          sceneBannerBuffer = [];
        };

        for (const { img, kind } of bannerImages) {
          const sceneId = img.moveToSceneId!;
          const label = buildButtonLabel(kind, sceneId);
          if (kind === "artifact") {
            flushSceneBanners();
            items.push(
              <SceneImageCard
                key={`scene-${img.id}`}
                image={img}
                buttonLabel={label}
                onEnterScene={onEnterScene!}
                className={styles.image}
              />,
            );
          } else {
            sceneBannerBuffer.push({
              img,
              buttonLabel: label,
              buttonIcon: <PersonSimpleRunIcon />,
            });
          }
        }
        flushSceneBanners();

        if (regularImages.length > 0) {
          items.push(
            <PhotoWidget
              key={`gallery-${i}`}
              items={regularImages.map(toPhoto)}
              onClick={(photos) => onPhotosClick(photos)}
              className={styles.image}
            />,
          );
        }

        if (items.length === 0) return null;
        if (items.length === 1) return items[0];
        return <Fragment key={`img-group-${i}`}>{items}</Fragment>;
      }

      return (
        <Markdown key={`text-${i}`} remarkPlugins={remarkPlugins} components={components}>
          {segment.content}
        </Markdown>
      );
    }),
    [segments, imageMap, components, onPhotosClick, onEnterScene, buildButtonLabel],
  );

  return <div className={styles.markdown}>{nodes}</div>;
};
